import { call, delay, race, select } from 'typed-redux-saga'
import { isAccessTokenValid } from 'APP/Lib/AuthHelpers'
import { logger } from 'APP/Lib/Datadog'
import { selectAccessToken } from 'APP/Redux/LoginRedux'

function* waitForValidAccessToken() {
  function* _doCheckTokenValidity() {
    const accessToken = yield* select(selectAccessToken)
    const isValid = yield* call(isAccessTokenValid, accessToken)

    return { accessToken, isValid }
  }

  const startTime = Date.now()
  let checkResult = yield* _doCheckTokenValidity()

  while (!checkResult.accessToken || !checkResult.isValid) {
    yield* delay(100)
    checkResult = yield* _doCheckTokenValidity()
  }

  const endTime = Date.now()
  logger.info('Access token is valid, continuing', {
    timeElapsed: endTime - startTime,
  })

  return checkResult.accessToken
}

/**
 * Helper saga that blocks until a valid access token is available in the store,
 * or we reach the provided timeout.
 * For what defines a "valid" token, see the corresponding `isAccessTokenValid` helper.
 * This may not be suitable for use cases where a limited token is acceptable.
 * @returns The access token that has been found valid, otherwise null.
 */
export function* waitForValidAccessTokenWithTimeout(
  /* The maximum amount of time to block for, before we time out. Defaults to 2000ms. */
  timeoutInMs: number = 2000
) {
  const { validAccessToken } = yield* race({
    validAccessToken: call(waitForValidAccessToken),
    timedOut: delay(timeoutInMs),
  })

  if (!validAccessToken) {
    logger.warn('Timed out while waiting for valid access token', {
      timeoutInMs,
    })
    return null
  }

  return validAccessToken
}
